import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiExternalLink, FiX, FiArrowRight } from 'react-icons/fi';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { appsData } from '../data/appsData';
import Logo from '../components/Logo';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionRef.current && !suggestionRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filterOptions = useMemo(() => {
    const categories = ['apa', 'ia', 'instr', 'plagio', 'prompts', 'prop', 'repo', 'lang'];
    const sorted = categories.sort((a, b) => t(`categories.${a}`).localeCompare(t(`categories.${b}`)));
    return ['all', ...sorted];
  }, [t, i18n.language]);

  // Motor de sugerencias basado en TODAS las palabras clave proporcionadas
  const suggestions = useMemo(() => {
    if (search.length < 2) return [];
    
    // Obtenemos todas las palabras clave de todas las apps según el idioma actual
    const allKeywords = appsData.flatMap(app => {
      const keywordsString = t(`apps.${app.id}.keywords`, { defaultValue: '' });
      return keywordsString.split(',').map(k => k.trim());
    });

    // Filtramos y eliminamos duplicados
    return [...new Set(allKeywords)]
      .filter(k => k.toLowerCase().includes(search.toLowerCase()))
      .slice(0, 10); // Mostramos hasta 10 sugerencias
  }, [search, t, i18n.language]);

  const filteredApps = useMemo(() => {
    return appsData.filter(app => {
      const title = t(`apps.${app.id}.title`).toLowerCase();
      const desc = t(`apps.${app.id}.desc`).toLowerCase();
      const keywords = t(`apps.${app.id}.keywords`).toLowerCase();
      
      const matchesSearch = title.includes(search.toLowerCase()) || 
                           desc.includes(search.toLowerCase()) || 
                           keywords.includes(search.toLowerCase());
      
      const matchesFilter = activeFilter === 'all' || app.categories.includes(activeFilter);
      
      return matchesSearch && matchesFilter;
    });
  }, [search, activeFilter, t, i18n.language]);

  return (
    <div className="min-h-screen">
      <section className="relative pt-24 pb-16 px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
          <div className="mb-8"><Logo className="text-8xl md:text-[10rem] py-4" /></div>
          <p className="text-xl md:text-2xl font-bold text-gray-500 dark:text-gray-400 italic mb-16 max-w-2xl mx-auto leading-relaxed">
            {t('common.slogan')}
          </p>

          {/* Buscador con Autocompletado */}
          <div className="relative w-full max-w-2xl mx-auto mb-20" ref={suggestionRef}>
            <div className="relative group">
              <FiSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-blue-500 text-2xl" />
              <input 
                type="text" 
                placeholder={t('common.search')}
                value={search}
                onFocus={() => setShowSuggestions(true)}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShowSuggestions(true);
                }}
                className="w-full pl-16 pr-14 py-7 rounded-[2.5rem] bg-white dark:bg-white/5 border-2 border-transparent focus:border-blue-500/30 shadow-sm dark:shadow-none dark:border-gray-800 outline-none text-xl font-medium transition-all text-gray-900 dark:text-white"
              />
              {search.length > 0 && (
                <button onClick={() => { setSearch(''); setShowSuggestions(false); }} className="absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400">
                  <FiX size={24} />
                </button>
              )}
            </div>

            <AnimatePresence>
              {showSuggestions && suggestions.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-3 bg-white dark:bg-[#1E293B] rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden z-50 py-2"
                >
                  {suggestions.map((s, idx) => (
                    <button 
                      key={idx} 
                      onClick={() => { setSearch(s); setShowSuggestions(false); }}
                      className="w-full px-8 py-4 text-left flex items-center justify-between hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors text-gray-700 dark:text-gray-300 font-bold"
                    >
                      <span className="truncate pr-4">{s}</span>
                      <FiArrowRight className="text-blue-500 shrink-0" />
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </section>

      {/* Filtros */}
      <section className="max-w-7xl mx-auto px-4 mb-20 text-center">
        <div className="flex flex-wrap justify-center gap-3">
          {filterOptions.map(c => (
            <button 
              key={c} 
              onClick={() => setActiveFilter(c)}
              className={`px-6 py-3 rounded-2xl text-sm font-black transition-all border-none outline-none ${activeFilter === c ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg' : 'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10'}`}
            >
              {t(`categories.${c}`)}
            </button>
          ))}
        </div>
      </section>

      {/* Grid de Apps */}
      <section className="max-w-7xl mx-auto px-4 pb-32">
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          <AnimatePresence mode="popLayout">
            {filteredApps.map((app) => (
              <motion.div 
                layout 
                key={app.id} 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0, scale: 0.95 }}
                className="group flex flex-col h-full bg-white dark:bg-[#1E293B] rounded-[3.5rem] p-10 border border-gray-100 dark:border-gray-800 hover:border-blue-500/30 transition-all shadow-sm"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/10 to-cyan-500/10 flex items-center justify-center mb-10 group-hover:scale-110 transition-transform duration-500">
                  <SafeIcon icon={FiIcons[app.icon]} className="text-3xl text-blue-600" />
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="flex flex-wrap gap-2 mb-6">
                    {app.categories.map(cat => (
                      <span key={cat} className="text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-600/5 px-2.5 py-1 rounded-lg">
                        {t(`categories.${cat}`)}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-3xl font-black mb-6 dark:text-white leading-tight">
                    {t(`apps.${app.id}.title`)}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-10 leading-relaxed font-medium text-lg flex-grow">
                    {t(`apps.${app.id}.desc`)}
                  </p>
                  <a 
                    href={app.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full py-5 rounded-[1.5rem] bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-black transition-all flex items-center justify-center gap-3 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:-translate-y-1 mt-auto"
                  >
                    <span>{t(`apps.${app.id}.btn`)}</span>
                    <FiExternalLink size={20} />
                  </a>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {filteredApps.length === 0 && (
          <div className="py-20 text-center">
            <p className="text-2xl text-gray-400 font-bold">{t('common.noResults')}</p>
          </div>
        )}
      </section>
    </div>
  );
}